import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { datahubAxiosClient, ENDPOINT_DETAILS } from 'constants/server/apiEndPoint';
import { mapCallProposalFromDto } from 'api/tasks/helpers';
import { ICallProposal } from 'api/tasks/models';
import { useLocalization } from 'localization';
import notifyHandler from 'utils/notifyHandler';
import { ordersQueryKey } from 'api/orders/query';
import QUERIES from 'modules/common/queries';

type BookingTypeParams = Partial<{
  changeToType: string;
  person: string;
  responsibleUser: string;
  taskId: string;
  bookingId: string;
}>;
type BookingPhotographerParams = Pick<BookingTypeParams, 'bookingId'> & {
  proposalId: string;
};

type ThirdPartyBookingParams = {
  caseId: string;
  realtorName: string;
  recipientEmail: string;
  recipientName: string;
  recipientPhone: string;
  taskId: string;
};

export const bookingsQueryKey = {
  all: ['bookings'] as const,
  getProposal: () => [...bookingsQueryKey.all, 'proposal'] as const,
};

export const getProposalApi = (bookingId: string) => {
  const { data, isLoading, isFetching, isError } = useQuery<ICallProposal | undefined>({
    queryKey: bookingsQueryKey.getProposal(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getProposalByBookingId(bookingId));
      return response?.data?.suggestedProposal ? mapCallProposalFromDto(response.data) : undefined;
    },
    enabled: !!bookingId,
    staleTime: 0,
  });

  return { data, isLoading, isFetching, isError };
};

export const updateBookingTypeMutation = () => {
  const { t } = useLocalization();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: BookingTypeParams) => {
      const { taskId = '', bookingId = '', ...rest } = params;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.updateBookingType(taskId, bookingId), {
        ...rest,
      });
      if (!response?.data?.success) {
        throw new Error('Error with update booking type');
      }
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.socialMediaSlideshow.error') });
    },
    onSuccess: () => {},
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const confirmBookingPhotographerMutation = () => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: BookingPhotographerParams) => {
      const { bookingId = '', proposalId } = params;
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.confirmBookingPhotographer(bookingId), {
        proposalId,
      });
      if (!response?.data?.success) {
        throw new Error('Error with confirm booking photographer');
      }
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.socialMediaSlideshow.error') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: ordersQueryKey.orderTasks() });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks, category] });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const confirmPersonBookingMutation = () => {
  const { t } = useLocalization();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (params: ThirdPartyBookingParams) => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.thirdPartyBooking(), { ...params });
      if (response?.status !== 200 || !response?.data?.success) {
        throw new Error('Booking failed');
      }
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.socialMediaSlideshow.error') });
    },
    onSuccess: () => {},
  });
  return { mutateAsync, isLoading, data, isSuccess };
};

export const markTaskAsDonePartiallyMutation = () => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading, data, isSuccess } = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await datahubAxiosClient.post(ENDPOINT_DETAILS.markDonePartially(taskId));
      return response;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.socialMediaSlideshow.error') });
      throw new Error('Mark the task as done failed');
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: ordersQueryKey.orderTasks() });
      queryClient.invalidateQueries({ queryKey: bookingsQueryKey.getProposal() });
      queryClient.invalidateQueries({ queryKey: [QUERIES.tasks] });
    },
  });
  return { mutateAsync, isLoading, data, isSuccess };
};
